'use client';

import Link from 'next/link';

import { CategoryBadge } from './CategoryBadge';
import { OddsBar } from './OddsBar';
import { createApiClient, type MarketListItem } from '@/lib/api';
import { formatTimeUntil, formatUsdt } from '@/lib/format';
import { getInitData, hapticImpact } from '@/lib/tma';

export function MarketCard({ market }: { market: MarketListItem }) {
  const totalPool = market.pool_yes_micro + market.pool_no_micro;
  const api = createApiClient(getInitData);
  const heroUrl = market.hero_image_r2_key ? api.mediaUrl(market.hero_image_r2_key) : null;

  const isOpen = market.state === 'open';
  const isResolvedYes = market.state === 'resolved_yes';
  const isResolvedNo = market.state === 'resolved_no';
  const isRefunded = market.state === 'refunded';
  const isFinalized = market.state === 'finalized';
  const isClosing = market.state === 'closing';

  return (
    <Link
      href={`/market?id=${encodeURIComponent(market.id)}`}
      onClick={() => hapticImpact('light')}
      className="block bg-bg-elev border border-line overflow-hidden transition-[border] active:scale-[0.99] duration-150"
    >
      {/* Hero image (если есть) */}
      {heroUrl && (
        <div className="relative w-full aspect-[16/9] bg-bg-elev2 overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={heroUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-hero-fade" />
        </div>
      )}

      {/* Top ribbon — category + status + time */}
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-line font-mono text-[10px] tracking-[0.1em]">
        <CategoryBadge category={market.category} />
        {market.country_code && (
          <span className="text-text-tertiary">{market.country_code}</span>
        )}
        <span className="flex-1" />
        {market.is_breaking === 1 && isOpen && (
          <span className="text-pink display tracking-[0.08em] text-[10px]">
            <span className="pulse-dot bg-pink mr-1" /> BREAKING
          </span>
        )}
        {isResolvedYes && (
          <span className="text-yes display tracking-[0.08em] text-[10px]">✓ ДА</span>
        )}
        {isResolvedNo && (
          <span className="text-no display tracking-[0.08em] text-[10px]">✗ НЕТ</span>
        )}
        {isRefunded && (
          <span className="text-text-secondary display tracking-[0.08em] text-[10px]">
            ↩ REFUND
          </span>
        )}
        {isFinalized && (
          <span className="text-text-tertiary display tracking-[0.08em] text-[10px]">FINAL</span>
        )}
        {isClosing && (
          <span className="text-warn display tracking-[0.08em] text-[10px]">⏱ ЗАКРЫВАЕТСЯ</span>
        )}
        {isOpen && (
          <span className="text-text-tertiary tabular-nums">
            ⏱ {formatTimeUntil(market.close_at)}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-3.5 pt-3.5">
        <h3 className="text-[16px] font-bold leading-tight tracking-[-0.01em] mb-3">
          {market.question_ru}
        </h3>
        <OddsBar
          poolYesMicro={market.pool_yes_micro}
          poolNoMicro={market.pool_no_micro}
          size="md"
        />
      </div>

      {/* Footer stats strip */}
      <div className="grid grid-cols-2 mt-3.5 border-t border-line font-mono text-[10px]">
        <div className="px-3.5 py-2 border-r border-line">
          <div className="text-text-tertiary text-[9px] tracking-[0.1em]">ПУЛ</div>
          <div className="text-white font-bold tabular-nums">{formatUsdt(totalPool)}</div>
        </div>
        <div className="px-3.5 py-2">
          <div className="text-text-tertiary text-[9px] tracking-[0.1em]">СТАВКИ</div>
          <div className="text-white font-bold tabular-nums">{market.bets_count}</div>
        </div>
      </div>
    </Link>
  );
}
